import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Import components
import Navbar from './components/Navbar';
import Home from './components/Home';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import Translate from './components/Translate';
import Profile from './components/Profile';
import PrivateRoute from './components/PrivateRoute';
import AdminDashboard from './admin/AdminDashboard';

// API base URL - this would be your deployed worker URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://saas-translation-api.scalethem.workers.dev';

// Create API context
export const ApiContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(localStorage.getItem('authToken'));

  // Load user profile on app start if token exists
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (authToken) {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/profile`, {
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setUser(data.user);
          } else {
            // Token is invalid, clear it
            localStorage.removeItem('authToken');
            setAuthToken(null);
            setUser(null);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          localStorage.removeItem('authToken');
          setAuthToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    fetchUserProfile();
  }, [authToken]);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('authToken', data.token);
        setAuthToken(data.token);
        setUser(data.user);
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.error };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, email, password })
      });

      if (response.ok) {
        const data = await response.json();
        return { success: true, user: data.user };
      } else {
        const error = await response.json();
        return { success: false, error: error.error };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setAuthToken(null);
    setUser(null);
  };

  const api = {
    user,
    login,
    register,
    logout,
    authToken,
    setAuthToken,
    setUser
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <ApiContext.Provider value={api}>
      <Router>
        <div className="App">
          <Navbar user={user} onLogout={logout} />
          <main className="main-content">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={!user ? <Login /> : <Navigate to="/dashboard" />} />
              <Route path="/register" element={!user ? <Register /> : <Navigate to="/dashboard" />} />
              <Route path="/dashboard" element={
                <PrivateRoute user={user}>
                  <Dashboard />
                </PrivateRoute>
              } />
              <Route path="/translate" element={
                <PrivateRoute user={user}>
                  <Translate />
                </PrivateRoute>
              } />
              <Route path="/profile" element={
                <PrivateRoute user={user}>
                  <Profile />
                </PrivateRoute>
              } />
              <Route path="/admin" element={<AdminDashboard />} />
            </Routes>
          </main>
        </div>
      </Router>
    </ApiContext.Provider>
  );
}

export default App;